import { z, ZodEffects } from 'zod';
import { EnglishTestSchema, StudySchema } from '../applicant/index.js';
import { RefereeSchema } from '../application/index.js';
import { PassportSchema } from '../shared/index.js';
import { ApplicantOfferPaymentMethod } from './enums.js';
import { defaultRefineFn, DepositAmountDetailSchema, SLCDetailSchema, SponsorshipDetailSchema } from './payment.js';
import { FeeStatus } from '../enums.js';
import { RecordedInterviewSystemStatus } from '../../recorded-interview/enums.js';

export const DocumentFulfilmentSchema = z.object({
  sKey: z.string().nullable(), // Allow null for deletion, required for uploads
  s3Keys: z.array(z.string()).nullable(), // Allow null for deletion, required for uploads
  urls: z.array(z.string()).optional(),
  linkText: z.string().optional(),
  isDeleted: z.boolean().optional(),
});

export const TextFulfilmentSchema = z.object({
  text: z.string().optional(),
  subtext: z.string().optional(),
});

export const PassportFulfilmentSchema = DocumentFulfilmentSchema;

export const OfferPassportSchema = PassportFulfilmentSchema.extend({
  passport: PassportSchema,
});

export const OfferPassportSchemaWithFiles = (fileNames?: string[]): z.ZodEffects<typeof OfferPassportSchema> => {
  return OfferPassportSchema.superRefine((_data, ctx) => {
    const hasFiles = fileNames && fileNames.length > 0;

    if (!hasFiles) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Passport file is required.',
        path: ['fileUpload'],
      });
    }
  });
};

// Generic document schema with file validation for offer pages
export const DocumentSchemaWithFiles = (
  fileNames?: string[],
  errorMessage: string = 'File is required.',
): z.ZodEffects<typeof DocumentFulfilmentSchema> => {
  return DocumentFulfilmentSchema.superRefine((_data, ctx) => {
    const hasFiles = fileNames && fileNames.length > 0;

    if (!hasFiles) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: errorMessage,
        path: ['fileUpload'],
      });
    }
  });
};

export const AcademicResultFulfilmentSchema = z.object({
  studyHistory: StudySchema,
});

export const EnglishResultsFulfilmentSchema = z.object({
  englishTest: EnglishTestSchema,
});

export const RefereeDetailsFulfilmentSchema = z.object({
  referees: z.array(RefereeSchema).min(1, 'At least one referee is required'),
});

export const OfferFinancePaymentFulfilmentSchema = z
  .object({
    paymentMethod: z.nativeEnum(ApplicantOfferPaymentMethod, { message: 'Please provide the value' }),
    depositDetail: DepositAmountDetailSchema.optional().nullish(),
    sponsorshipDetail: SponsorshipDetailSchema.optional().nullish(),
    slcDetail: SLCDetailSchema.optional().nullish(),
    statusInternalNote: z.string().optional().nullish(),
  })
  .superRefine((paymentDetail, ctx) => {
    defaultRefineFn(paymentDetail, ctx);
  });

export function enhanceOfferFinancePaymentFulfilmentSchema(
  feeStatus: FeeStatus,
): ZodEffects<typeof OfferFinancePaymentFulfilmentSchema> | typeof OfferFinancePaymentFulfilmentSchema {
  if (feeStatus !== FeeStatus.Home) {
    return OfferFinancePaymentFulfilmentSchema.superRefine((data, ctx) => {
      const { paymentMethod } = data;

      defaultRefineFn(data, ctx);
      if (paymentMethod === ApplicantOfferPaymentMethod.SLCFunding) {
        ctx.addIssue({
          code: 'custom',
          path: ['slcDetail'],
          message: 'Only home student can apply for student loan company',
        });
      }
    });
  }
  return OfferFinancePaymentFulfilmentSchema;
}

/* eslint-disable @typescript-eslint/no-empty-object-type */

export interface IDocumentFulfilmentData extends z.infer<typeof DocumentFulfilmentSchema> {}
export interface ITextFulfilmentData extends z.infer<typeof TextFulfilmentSchema> {}

export interface IPassportFulfilmentData extends z.infer<typeof PassportFulfilmentSchema> {}
export interface IAcademicResultFulfilmentData extends z.infer<typeof AcademicResultFulfilmentSchema> {}
export interface IEnglishResultsFulfilmentData extends z.infer<typeof EnglishResultsFulfilmentSchema> {}
export interface IRefereeDetailsFulfilmentData extends z.infer<typeof RefereeDetailsFulfilmentSchema> {}
export interface IOfferFinancePaymentFulfilmentData extends z.infer<typeof OfferFinancePaymentFulfilmentSchema> {}

export interface IOfferPassportData extends z.infer<typeof OfferPassportSchema> {}

export const InternalDocumentSchema = z.object({
  s3Keys: z.array(z.string()),
  fileNames: z.array(z.string()).optional(),
  urls: z.array(z.string()).optional(),
});

export interface IInternalDocumentData extends z.infer<typeof InternalDocumentSchema> {}

export type ApplyRecordingStatus = Extract<
  RecordedInterviewSystemStatus,
  | RecordedInterviewSystemStatus.ATTEMPTED
  | RecordedInterviewSystemStatus.RECORDED
  | RecordedInterviewSystemStatus.EXCEEDED
>;

export interface IRecordedInterviewFulfilmentData {
  interviewId: string;
  username: string;
  hash: string;
  hashTimestamp: number;
  videoId?: string;
  questions?: string;
  welcomeTitle?: string;
  recordingStatus?: ApplyRecordingStatus;
  hasMismatchedQuestions?: boolean;
  attempts: Record<string, { timestamp: number; recorded: boolean; attemptIndex: number }>;
}
